/**
 * ReadinessSlot — reserved biometric headroom (direction §3, decision 7).
 *
 * Readiness ("am I ready for TODAY's session") is intrinsic to the loop,
 * so the slot lives IN the hero meta region — not a future separate
 * widget. We reserve it now so wiring Watch/Garmin/Oura/RingConn later is
 * a content swap, not a redesign.
 *
 * - Unwired (ships now): a striped, honestly-empty placeholder reading
 *   "READINESS · Connect a wearable" with the four source names as a
 *   monospace hint. No painted score (decision 6 — never fake data).
 * - Wired (future): pass `score` + `band` → solid border, paper fill,
 *   "READINESS {score} · {band}". Identical footprint. (Not built/used
 *   yet; the prop shape is reserved so the eng wire-up needs no layout
 *   change.)
 */
import styles from './ReadinessSlot.module.css';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';

/** A2 — `sys.wearable` @13. */
const WatchGlyph = () => {
  const iconsOn = useIconSystem();
  if (iconsOn) return <Icon name="sys.wearable" size={13} />;
  return (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <rect x="6" y="6" width="12" height="12" rx="3" />
    <path d="M9 3h6M9 21h6" />
  </svg>
  );
};

export type ReadinessBand = 'primed' | 'caution' | 'low';

export interface ReadinessSlotProps {
  /** Future wired state — a readiness score (0–100). Omit → unwired. */
  score?: number;
  /** Future wired band; maps to --readiness-band-{primed,caution,low}. */
  band?: ReadinessBand;
  className?: string;
}

const BAND_LABEL: Record<ReadinessBand, string> = {
  primed: 'primed',
  caution: 'hold back',
  low: 'recover',
};

export function ReadinessSlot({ score, band, className }: ReadinessSlotProps) {
  const wired = typeof score === 'number';
  const cls = [styles.readiness, wired ? styles.isWired : '', className].filter(Boolean).join(' ');

  return (
    <div className={cls} aria-label={wired ? `Readiness ${score}` : 'Readiness, connect a wearable'}>
      <span className={styles.glyph}>
        <WatchGlyph />
      </span>
      <span className={styles.txt}>
        <span className={styles.lbl}>Readiness</span>
        {wired && band ? (
          <span className={styles.hint}>
            <span className={styles.score}>{score}</span>{' '}
            <span className={`${styles.band} ${styles[band]}`}>· {BAND_LABEL[band]}</span>
          </span>
        ) : (
          <span className={styles.hint}>
            Connect <code>Watch · Garmin · Oura · RingConn</code>
          </span>
        )}
      </span>
    </div>
  );
}
